import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const theme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: '#1976d2',
      },
      secondary: {
        main: '#dc004e',
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
            minHeight: '100vh',
            '@supports (-webkit-touch-callout: none)': {
              minHeight: '-webkit-fill-available',
            },
          },
          body: {
            minHeight: '100vh',
            '@supports (-webkit-touch-callout: none)': {
              minHeight: '-webkit-fill-available',
            },
          },
          '#root': {
            minHeight: '100vh',
            '@supports (-webkit-touch-callout: none)': {
              minHeight: '-webkit-fill-available',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            minHeight: 44,
            '@media (max-width: 600px)': {
              minHeight: 48,
              fontSize: '1rem',
              padding: '12px 24px',
            },
          },
          sizeSmall: {
            '@media (max-width: 600px)': {
              minHeight: 40,
              fontSize: '0.875rem',
              padding: '8px 16px',
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '@media (max-width: 600px)': {
              width: 48,
              height: 48,
            },
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            '@media (max-width: 600px)': {
              overflowX: 'auto',
            },
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            '@media (max-width: 600px)': {
              minWidth: 'auto',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            '@media (max-width: 600px)': {
              padding: '12px 8px',
              fontSize: '0.875rem',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            '@media (max-width: 600px)': {
              minHeight: 32,
              fontSize: '0.875rem',
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            '@media (max-width: 600px)': {
              margin: '16px',
              width: 'calc(100% - 32px)',
              maxHeight: 'calc(100% - 32px)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            '&.chart-container': {
              '@media (max-width: 600px)': {
                padding: '16px !important',
                height: '300px !important',
              },
              '@media (min-width: 601px)': {
                padding: '24px !important',
                height: '400px !important',
              },
            },
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            '@media (max-width: 600px)': {
              bottom: '80px !important',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '@media (max-width: 600px)': {
              '& .MuiOutlinedInput-root': {
                minHeight: 56,
                fontSize: '1rem',
              },
              '& .MuiInputLabel-root': {
                fontSize: '1rem',
              },
            },
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            '@media (max-width: 600px)': {
              minHeight: 56,
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            '@media (max-width: 600px)': {
              minHeight: 24,
              fontSize: '1rem',
              paddingTop: 16,
              paddingBottom: 16,
            },
          },
        },
      },
    },
  });

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};